import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="dialog"
export default class extends Controller {
  static targets = [
    "modal",
    "backdrop",
    "content",
    "title",
    "message",
    "confirmButton",
    "cancelButton",
  ];
  static values = {
    confirmText: { type: String, default: "Confirm" },
    cancelText: { type: String, default: "Cancel" },
    confirmClass: {
      type: String,
      default: "bg-red-600 hover:bg-red-700 text-white",
    },
    cancelClass: {
      type: String,
      default: "bg-gray-300 hover:bg-gray-400 text-gray-800",
    },
  };

  connect() {
    // Bind methods to preserve context
    this.handleEscape = this.handleEscape.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);

    // Set up initial state
    this.isOpen = false;
    this.previouslyFocused = null;
  }

  disconnect() {
    this.removeEventListeners();
  }

  open(event) {
    if (event) event.preventDefault();

    this.isOpen = true;
    this.previouslyFocused = document.activeElement;

    // Update dialog content if data attributes are provided
    this.updateContent(event);

    // Show modal
    this.modalTarget.classList.remove("hidden");
    this.modalTarget.setAttribute("aria-hidden", "false");

    // Add event listeners
    this.addEventListeners();

    // Focus management
    this.trapFocus();

    // Prevent body scroll
    document.body.style.overflow = "hidden";
  }

  close(event) {
    if (event) event.preventDefault();

    this.isOpen = false;

    // Hide modal
    this.modalTarget.classList.add("hidden");
    this.modalTarget.setAttribute("aria-hidden", "true");

    // Remove event listeners
    this.removeEventListeners();

    // Restore focus
    if (this.previouslyFocused) {
      this.previouslyFocused.focus();
      this.previouslyFocused = null;
    }

    // Restore body scroll
    document.body.style.overflow = "";
  }

  confirm(event) {
    event.preventDefault();

    // Get the trigger element that opened the dialog
    const triggerElement = this.previouslyFocused;

    // If the trigger element has a form, submit it
    if (triggerElement) {
      const form = triggerElement.closest("form");
      const href = triggerElement.getAttribute("href");
      const method =
        triggerElement.getAttribute("data-method") ||
        triggerElement.getAttribute("data-turbo-method");

      if (form) {
        // Submit the form
        form.submit();
      } else if (href) {
        // Handle link navigation with method
        if (method && method.toLowerCase() !== "get") {
          this.submitMethodForm(href, method);
        } else {
          window.location.href = href;
        }
      }
    }

    this.close();
  }

  cancel(event) {
    this.close(event);
  }

  handleEscape(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close();
    }
  }

  handleBackdropClick(event) {
    if (event.target === this.backdropTarget) {
      this.close();
    }
  }

  // Private methods
  updateContent(event) {
    const triggerElement = event?.target || this.previouslyFocused;

    if (triggerElement) {
      // Update title
      const title = triggerElement.dataset.dialogTitle;
      if (title && this.hasTitleTarget) {
        this.titleTarget.textContent = title;
      }

      // Update message
      const message = triggerElement.dataset.dialogMessage;
      if (message && this.hasMessageTarget) {
        this.messageTarget.textContent = message;
      }

      // Update button text
      const confirmText =
        triggerElement.dataset.dialogConfirm || this.confirmTextValue;
      if (this.hasConfirmButtonTarget) {
        this.confirmButtonTarget.textContent = confirmText;
      }

      const cancelText =
        triggerElement.dataset.dialogCancel || this.cancelTextValue;
      if (this.hasCancelButtonTarget) {
        this.cancelButtonTarget.textContent = cancelText;
      }

      // Update button classes
      const confirmClass =
        triggerElement.dataset.dialogConfirmClass || this.confirmClassValue;
      const cancelClass =
        triggerElement.dataset.dialogCancelClass || this.cancelClassValue;

      if (this.hasConfirmButtonTarget) {
        this.confirmButtonTarget.className = `px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmClass}`;
      }

      if (this.hasCancelButtonTarget) {
        this.cancelButtonTarget.className = `px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${cancelClass}`;
      }
    }
  }

  addEventListeners() {
    document.addEventListener("keydown", this.handleEscape);
    this.backdropTarget.addEventListener("click", this.handleBackdropClick);
  }

  removeEventListeners() {
    document.removeEventListener("keydown", this.handleEscape);
    this.backdropTarget.removeEventListener("click", this.handleBackdropClick);
  }

  trapFocus() {
    // Simple focus trap - focus the first focusable element in the dialog
    const focusableElements = this.modalTarget.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  submitMethodForm(url, method) {
    // Create a temporary form for non-GET requests
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;

    // Add CSRF token
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add method override for non-POST methods
    if (method.toLowerCase() !== "post") {
      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = method.toUpperCase();
      form.appendChild(methodInput);
    }

    // Submit the form
    document.body.appendChild(form);
    form.submit();
  }
}
